import { StaleRecipeWriteError } from '../../../../services/recipeWriteConflict';
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, Loader2 } from "lucide-react";
import { primaryText, secondaryText, greyTextColor, borderPrimary, greenColorClass } from "../../../../styling/stylingHelper";
import { candidateUnit, MatchingIngredientRef, SwapReviewAdapter, SwapCandidate } from "./reviewAdapter";

interface ApplyToOtherItemsPanelProps {
    productMatching?: boolean;
    templateName: string;
    candidate: SwapCandidate;
    matches: MatchingIngredientRef[];
    adapter: SwapReviewAdapter;
    onDone: () => void;
}

/**
 * Post-swap convenience: the automation reuses the same template ingredient name
 * across items, so one correction ("bread" → the right product) almost always
 * applies to every other dish/subrecipe carrying that ingredient. Offers those
 * items with everything pre-selected; each gets its OWN template quantity converted
 * into the new product's unit, so a 20 g and a 150 g usage don't end up identical.
 */
const ApplyToOtherItemsPanel: React.FC<ApplyToOtherItemsPanelProps> = ({
    templateName,
    productMatching = false,
    candidate,
    matches,
    adapter,
    onDone,
}) => {
    const { t } = useTranslation();
    const [selectedIds, setSelectedIds] = useState<Set<string>>(
        () => new Set(matches.map(m => `${m.itemId}-${m.ingredientIndex}`))
    );
    const [quantities, setQuantities] = useState<Record<string, string>>({});
    const [completed, setCompleted] = useState<Set<string>>(new Set());
    const [isApplying, setIsApplying] = useState(false);
    const [failedCount, setFailedCount] = useState(0);

    const refKey = (match: MatchingIngredientRef) => `${match.itemId}-${match.ingredientIndex}`;
    const unit = candidateUnit(candidate) ?? '';

    const toggle = (key: string) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
        });
    };

    const remaining = matches.filter(match => !completed.has(refKey(match)));
    const allSelected = selectedIds.size === remaining.length;
    const quantityFor = (match: MatchingIngredientRef) => match.requiresQuantity
        ? Number((quantities[refKey(match)] ?? '').replace(',', '.')) : match.quantity;
    const invalidQuantity = remaining.some(match => selectedIds.has(refKey(match)) &&
        (!Number.isFinite(quantityFor(match)) || quantityFor(match) <= 0));

    const handleApply = async () => {
        if (isApplying || invalidQuantity) return;
        const selected = remaining.filter(m => selectedIds.has(refKey(m)));
        if (selected.length === 0) {
            onDone();
            return;
        }

        setIsApplying(true);
        let failures = 0;
        // Sequential on purpose: each item is a read-modify-write of its own
        // record, and a burst of parallel writes buys nothing here.
        for (const match of selected) {
            try {
                await adapter.swap(match.itemId, match.ingredient, match.ingredientIndex, candidate, quantityFor(match));
                const key = refKey(match);
                setCompleted(prev => new Set(prev).add(key));
                setSelectedIds(prev => { const next = new Set(prev); next.delete(key); return next; });
            } catch (err) {
                console.error(`Failed to apply swap to item ${match.itemId}:`, err);
                if (err instanceof StaleRecipeWriteError) alert(err.message);
                failures += 1;
            }
        }
        setIsApplying(false);

        if (failures > 0) {
            setFailedCount(failures);
            return;
        }
        onDone();
    };

    return (
        <div className={`mt-2 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 p-3 space-y-3`}>
            <div>
                <p className={`text-[14px] font-semibold ${primaryText}`}>
                    {t('costPrice.automationReview.applyToOthersTitle', {
                        ingredient: templateName,
                        product: candidate.displayName,
                    })}
                </p>
                <p className={`text-[12px] ${greyTextColor} mt-0.5`}>
                    {t(productMatching ? 'costPrice.automationRun.editor.sameProductDishes' : 'costPrice.automationReview.applyToOthersSubtitle', { count: new Set(matches.map(match => match.itemId)).size })}
                </p>
            </div>

            <button
                type="button"
                disabled={isApplying}
                onClick={() => setSelectedIds(allSelected ? new Set() : new Set(remaining.map(refKey)))}
                className={`text-[12px] font-semibold ${secondaryText} hover:opacity-70`}
            >
                {allSelected
                    ? t('costPrice.automationReview.deselectAll')
                    : t('costPrice.automationReview.selectAll')}
            </button>

            <div className="max-h-56 overflow-y-auto space-y-1.5">
                {remaining.map(match => {
                    const key = refKey(match);
                    const isSelected = selectedIds.has(key);
                    return (
                        <div
                            key={key}
                            className={`w-full flex items-center gap-3 rounded-lg border px-3 py-2 text-left transition-colors ${
                                isSelected
                                    ? 'border-darkGreenColor dark:border-darkGreenColor-dark bg-[#F1FAF7] dark:bg-green-900/20'
                                    : borderPrimary + ' bg-background dark:bg-background-dark'
                            }`}
                        >
                            <button type="button" role="checkbox" aria-checked={isSelected} aria-label={match.itemName}
                                disabled={isApplying} onClick={() => toggle(key)}
                                className={`h-11 w-11 rounded border flex items-center justify-center flex-shrink-0 ${
                                isSelected ? 'bg-darkGreenColor dark:bg-darkGreenColor-dark border-darkGreenColor dark:border-darkGreenColor-dark' : 'border-borderGrey dark:border-borderGrey-dark'
                            }`}>
                                {isSelected && <Check className="h-3 w-3 text-white dark:text-white" />}
                            </button>
                            <span className="min-w-0 flex-1">
                                <span className={`block text-[14px] font-medium ${primaryText} truncate`}>{match.itemName}</span>
                                <span className={`block text-[12px] ${greyTextColor} truncate`}>
                                    {match.currentMatchName
                                        ? t('costPrice.automationReview.applyToOthersCurrent', { name: match.currentMatchName })
                                        : t('costPrice.automationReview.applyToOthersNoMatch')}
                                </span>
                            </span>
                            <span className={`text-[12px] ${greyTextColor} flex-shrink-0`}>
                                {match.requiresQuantity ? <label className="flex flex-wrap items-center gap-1">
                                    <span className="sr-only">{t('costPrice.automationRun.editor.replacementQuantity', { dish: match.itemName, unit })}</span>
                                    <input aria-label={t('costPrice.automationRun.editor.replacementQuantity', { dish: match.itemName, unit })} inputMode="decimal" value={quantities[key] ?? ''} disabled={isApplying}
                                        onChange={event => setQuantities(prev => ({ ...prev, [key]: event.target.value }))}
                                        className={`h-11 w-20 rounded-lg border px-2 ${borderPrimary} bg-background dark:bg-background-dark ${primaryText}`} />
                                    {unit}
                                </label> : <>{match.quantity} {unit}</>}
                            </span>
                        </div>
                    );
                })}
            </div>

            {remaining.some(match => match.requiresQuantity) && <p className={`text-[12px] ${secondaryText}`}>{t('costPrice.automationRun.editor.replacementUnitChanged')}</p>}
            {failedCount > 0 && (
                <p className="text-[13px] text-red-600 dark:text-red-400">
                    {t('costPrice.automationReview.applyToOthersFailed', { count: failedCount })}
                </p>
            )}

            <div className="flex items-center gap-3">
                <button
                    type="button"
                    onClick={onDone}
                    disabled={isApplying}
                    className={`text-[13px] font-medium ${greyTextColor} hover:opacity-70 disabled:opacity-50`}
                >
                    {t(completed.size > 0 ? 'common.close' : productMatching ? 'costPrice.automationRun.editor.onlyThisDish' : 'costPrice.automationReview.applyToOthersSkip')}
                </button>
                <button
                    type="button"
                    onClick={handleApply}
                    disabled={isApplying || selectedIds.size === 0 || invalidQuantity}
                    className={`ml-auto h-9 px-4 justify-center text-[14px] font-semibold ${greenColorClass} disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2`}
                >
                    {isApplying && <Loader2 className="h-4 w-4 animate-spin" />}
                    {isApplying
                        ? t('costPrice.automationReview.applyToOthersApplying')
                        : t('costPrice.automationReview.applyToOthersConfirm', { count: new Set(remaining.filter(match => selectedIds.has(refKey(match))).map(match => match.itemId)).size })}
                </button>
            </div>
        </div>
    );
};

export default ApplyToOtherItemsPanel;
